<template>
  <div class="entry-wizard">
  <step-wizard
    v-model="activeStep"
    class="entry-wizard__steps"
    :steps="steps"
    :can-go-back="!anySent && !busy"
  >
    <template #mode>
      <div v-loading="!page" class="entry-step">
        <template v-if="page">
          <div class="entry-info">
            Хранится на текущий момент:
            <b>{{ page.amount }} {{ unitName }}</b>
            <template v-if="hasStorage">
              (из них направлено на хранение:
              <b>{{ round(page.sumAmountGet) }} {{ unitName }}</b>)
            </template>
          </div>
          <choice-cards
            :value="mode"
            :groups="modeGroups"
            @input="pickMode"
          ></choice-cards>
        </template>
      </div>
    </template>

    <!-- Даты слева, количество и поля справа, снизу - сохранение или дальнейшее действие -->
    <template #entry>
      <div class="entry-screen is-growing">
        <div class="entry-screen__main">
          <div class="entry-screen__side">
            <dates-calendar
              ref="calendar"
              v-model="dates"
              class="entry-screen__calendar"
              :is-disabled="isDateLocked"
              :marked="dateProblems"
            ></dates-calendar>
          </div>
          <!-- Поступление: поля режима «Поступило от». Расход: поля режима вместе с количеством -->
          <div class="entry-screen__table-box">
            <entry-table
              ref="table"
              v-loading="optionsLoading > 0"
              class="entry-screen__table"
              :dates.sync="dates"
              :amounts.sync="amounts"
              :unit="unitName"
              :available="isIncoming ? null : available"
              :fields="isIncoming ? modeFields : detailFields"
              :values="isIncoming ? modeValues : detailValues"
              :show-errors="showErrors"
              empty-text="Выберите даты в календаре слева"
              @update:values="setEntryValues"
              @passport="openPassport"
            ></entry-table>
          </div>
        </div>
        <div v-if="isIncoming" class="entry-screen__actions">
          <choice-cards
            compact
            :value="action"
            :groups="actionGroups"
            :loading="busy"
            @input="pickAction"
          ></choice-cards>
        </div>
        <!-- Расход: дальнейших действий нет - «Отмена» и «Сохранить» под всеми блоками справа -->
        <div v-else class="entry-screen__actions is-plain">
          <choice-cards
            compact
            :groups="saveGroups"
            :loading="busy"
            @input="onSaveCard($event, saveEntry)"
          ></choice-cards>
        </div>
      </div>
    </template>

    <template #details>
      <div class="entry-screen">
        <div class="entry-screen__main">
          <entry-table
            ref="table"
            v-loading="optionsLoading > 0"
            class="entry-screen__table"
            :dates.sync="dates"
            :amounts.sync="amounts"
            amounts-locked
            :unit="unitName"
            :fields="detailFields"
            :values.sync="detailValues"
            :skippable="isIncoming"
            :removable="!anySent"
            :show-errors="showErrors"
            @passport="openPassport"
          ></entry-table>
        </div>
        <div class="entry-screen__actions is-plain">
          <choice-cards
            compact
            :groups="saveGroups"
            :loading="busy"
            @input="onSaveCard($event, saveDetails)"
          ></choice-cards>
        </div>
      </div>
    </template>
  </step-wizard>

  <el-dialog
    title="Сопроводительный паспорт"
    :visible="passportDate !== null"
    width="55%"
    append-to-body
    destroy-on-close
    @close="passportDate = null"
  >
    <disposal-pasport-form
      v-if="passportDate"
      :action="passport"
      :waste="passportWastes"
      @addPasportToTable="onPassport"
    ></disposal-pasport-form>
  </el-dialog>
  </div>
</template>

<script>
import StepWizard from "@/components/ecologist/dataEntryNew/stepWizard";
import ChoiceCards from "@/components/ecologist/dataEntryNew/choiceCards";
import DatesCalendar from "@/components/ecologist/dataEntryNew/datesCalendar";
import EntryTable from "@/components/ecologist/dataEntryNew/entryTable";
import DisposalPasportForm from "@/components/ecologist/disposal/disposalPasport";
import {
  ACTIONS,
  INCOMING,
  MODES,
  OPTIONS_FOR,
  OUTGOING,
  buildContractorPayload,
  buildPayload,
  emptyPassport,
  fieldsFor,
  isAvailable,
  isIncoming,
  rowStatus,
} from "./entryModes";

// Выполнить task для всех items, не больше limit запросов одновременно
async function runLimited(items, limit, task) {
  let index = 0;
  const worker = async () => {
    while (index < items.length) await task(items[index++]);
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
}

const isTimeout = (error) =>
  error.code === "ECONNABORTED" ||
  (error.message && error.message.includes("timeout"));

// Карточка «Только сохранить» среди дальнейших действий
const SAVE_ONLY = "saveOnly";
// Карточка «Отмена» под каждой кнопкой сохранения
const CANCEL = "cancel";
const CANCEL_CARD = { type: CANCEL, title: "Отмена", icon: "el-icon-close", danger: true };
// Пауза после выбора дат перед их проверкой на сервере - пока протягивают интервал, не проверяем
const CHECK_DELAY = 300;

const SUBCLASSIFIERS = { pss17: 17, pss19: 19, pss20: 20, pss21: 21, pss22: 22 };

export default {
  name: "entryWizard",
  components: {
    StepWizard,
    ChoiceCards,
    DatesCalendar,
    EntryTable,
    DisposalPasportForm,
  },
  props: {
    // Узел отхода из дерева: techProcessHasWasteId, organizationWasteId, unitId
    waste: { type: Object, required: true },
  },
  data() {
    return {
      page: null,
      activeStep: 0,
      mode: null,
      // Дальнейшее действие после поступления
      action: null,
      dates: [],
      // Внесённые количества { [date]: "0.123" }
      amounts: {},
      // Поля режима «Поступило от» и поля действия { [date]: { ... } }
      modeValues: {},
      detailValues: {},
      // Проверки дат на сервере { [date]: { problem, available, lastDate } }
      checks: {},
      options: {
        contractors: [],
        contractorsByDate: [],
        pss17: [],
        pss19: [],
        pss20: [],
        pss21: [],
        pss22: [],
        transferOrgs: [],
        storageOrgs: [],
      },
      loaded: {},
      optionsLoading: 0,
      showErrors: false,
      // Уже отправленные записи { "type:date": true } - повторно их не шлём
      sent: {},
      // Выполняемое действие (карточка или "save") - кнопка крутится, этапы не переключаются
      busy: null,
      // Предупреждение о перерасчёте за прошедшие даты показывается один раз
      recalcNoticed: false,
      passportDate: null,
      passport: null,
      // Отход для полей тары в форме паспорта (только у передачи контрагенту)
      passportWastes: null,
      // Дата строки, паспорт которой заполнили последним: следующий открывается его копией
      lastPassportDate: null,
    };
  },

  computed: {
    isIncoming() {
      return isIncoming(this.mode);
    },
    // Режим, поля которого заполняются на последнем этапе
    detailType() {
      return this.isIncoming ? this.action : this.mode;
    },
    // Режим → даты с количеством (у расхода - и с полями режима, этап назван по режиму).
    // Поступление: этап полей появляется, только когда нажали кнопку дальнейшего действия
    steps() {
      const outgoing = this.mode && !this.isIncoming;
      const steps = [
        { name: "mode", title: "Режим" },
        {
          name: "entry",
          title: outgoing ? MODES[this.mode].stepTitle : "Даты и количество",
        },
      ];
      if (this.isIncoming && this.action)
        steps.push({ name: "details", title: MODES[this.action].stepTitle });
      return steps;
    },
    // Есть что терять при переключении на другой элемент дерева
    isDirty() {
      return this.activeStep > 0 || !!this.mode || this.dates.length > 0;
    },
    // Есть что терять при смене режима
    hasEntered() {
      return this.dates.length > 0 || Object.keys(this.amounts).length > 0;
    },
    anySent() {
      return Object.keys(this.sent).length > 0;
    },

    unitName() {
      return this.page && this.page.pss93UnitInfo
        ? this.page.pss93UnitInfo.name
        : "";
    },
    hasStorage() {
      return isAvailable("inStorage", this.page);
    },
    // Как в dataEntryEdit: списывать можно, только если есть остаток
    noRemainder() {
      return (
        this.round(this.page.amount) - this.round(this.page.sumAmountGet) <= 0
      );
    },
    modeGroups() {
      const card = (type) => ({
        type,
        title: MODES[type].title,
        icon: MODES[type].icon,
      });
      return [
        { title: "Поступление", items: INCOMING.map(card) },
        {
          title: "Расход",
          items: OUTGOING.filter((type) => isAvailable(type, this.page)).map(
            (type) => ({
              ...card(type),
              disabled: this.noRemainder,
              reason: "Нет остатка отхода для списания",
            })
          ),
        },
      ];
    },
    // Сохранение с отменой - справа, как «Сохранить» у расхода
    actionGroups() {
      return [
        {
          title: "Дальнейшее действие",
          items: ACTIONS.filter((type) => isAvailable(type, this.page)).map(
            (type) => ({
              type,
              title: MODES[type].actionTitle || MODES[type].title,
              icon: MODES[type].icon,
            })
          ),
        },
        {
          title: "Без действия",
          vertical: true,
          items: [
            {
              type: SAVE_ONLY,
              title: "Только сохранить",
              icon: "el-icon-check",
            },
            CANCEL_CARD,
          ],
        },
      ];
    },
    // «Отмена» и справа «Сохранить» - того же вида, что карточки дальнейших действий
    saveGroups() {
      return [
        {
          items: [
            CANCEL_CARD,
            { type: "save", title: "Сохранить", icon: "el-icon-check" },
          ],
        },
      ];
    },

    modeFields() {
      return this.isIncoming ? fieldsFor(this.mode, this.options) : [];
    },
    detailFields() {
      return this.detailType ? fieldsFor(this.detailType, this.options) : [];
    },
    /*
      Доступно на дату = остаток на эту дату с сервера минус количества строк выше:
      даты идут по возрастанию, и каждое списание уменьшает остаток на все следующие даты.
      Иначе несколько строк «по максимуму» вместе уводят остаток в минус
    */
    available() {
      const first = this.dates.find((date) => this.amounts[date] !== undefined);
      const available = {};
      let spent = 0;
      this.dates.forEach((date) => {
        const check = this.checks[date];
        if (!check || check.available === null) return;
        available[date] = check.available - spent;
        // Как в таблице: своё количество или серое предложенное из первой внесённой строки
        const own =
          this.amounts[date] !== undefined
            ? this.amounts[date]
            : first && this.amounts[first];
        const amount = parseFloat(own);
        if (!isNaN(amount)) spent += amount;
      });
      return available;
    },
    dateProblems() {
      const problems = {};
      this.dates.forEach((date) => {
        const check = this.checks[date];
        if (check && check.problem) problems[date] = check.problem;
      });
      return problems;
    },
  },

  watch: {
    // Даты проверяются сразу при выборе: проблемные краснеют в календаре, у расхода появляется остаток
    dates() {
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(this.checkInBackground, CHECK_DELAY);
    },
    mode(mode) {
      this.loadOptions(mode);
    },
    action(action) {
      this.detailValues = {};
      this.loadOptions(action);
    },
    activeStep() {
      this.showErrors = false;
    },
  },

  methods: {
    round(value) {
      return Math.round(value * 100000) / 100000;
    },

    isDateLocked(date) {
      return this.$store.getters.GETISDATEENTRYLOCKED(date);
    },

    stepIndex(name) {
      return this.steps.findIndex((step) => step.name === name);
    },

    // Одно действие за раз: пока выполняется, остальные кнопки не срабатывают
    async withBusy(key, task) {
      if (this.busy) return;
      this.busy = key;
      try {
        await task();
      } finally {
        this.busy = null;
      }
    },

    async pickMode(mode) {
      if (await this.changeMode(mode)) this.activeStep = this.stepIndex("entry");
    },

    // Поступление: «Только сохранить» сохраняет сразу, дальнейшее действие ведёт к его полям
    pickAction(type) {
      if (type === CANCEL) return this.cancel();
      return this.withBusy(type, async () => {
        if (!(await this.validateEntry())) return;
        if (type === SAVE_ONLY) {
          this.action = null;
          await this.save();
          return;
        }
        this.action = type;
        this.activeStep = this.stepIndex("details");
      });
    },

    // Расход сохраняется прямо с экрана дат
    saveEntry() {
      return this.withBusy("save", async () => {
        if (await this.validateEntry()) await this.save();
      });
    },

    saveDetails() {
      return this.withBusy("save", this.save);
    },

    onSaveCard(type, save) {
      return type === CANCEL ? this.cancel() : save();
    },

    // Отмена сбрасывает всё введённое: мастер пересоздаётся и начинается с выбора режима
    async cancel() {
      if (this.busy) return;
      if (this.hasEntered) {
        try {
          await this.confirm(
            "Внимание",
            "Введённые данные не сохранены и будут потеряны. Отменить внесение?",
            "warning",
            "Да",
            "Нет"
          );
        } catch {
          return;
        }
      }
      this.$emit("cancel");
    },

    // Таблица экрана дат заполняет поля режима поступления или поля расхода
    setEntryValues(values) {
      if (this.isIncoming) this.modeValues = values;
      else this.detailValues = values;
    },

    // Смена режима стирает всё введённое: поля и проверки у режимов разные.
    // Возвращает false, если пользователь отказался
    async changeMode(mode) {
      if (mode === this.mode) return true;
      if (this.hasEntered) {
        try {
          await this.confirm(
            "Внимание",
            "Выбранные даты и введённые количества будут потеряны. Сменить режим?",
            "warning",
            "Да",
            "Нет"
          );
        } catch {
          return false;
        }
      }
      this.mode = mode;
      this.action = null;
      this.dates = [];
      this.amounts = {};
      this.modeValues = {};
      this.detailValues = {};
      return true;
    },

    warn(message) {
      this.$message.warning(message);
      return false;
    },

    // Предупредить и прокрутить таблицу этапа к первой строке с ошибкой
    warnInTable(message) {
      this.$nextTick(() => {
        const table = this.$refs.table;
        if (table) table.scrollToFirstError();
      });
      return this.warn(message);
    },

    async loadPage() {
      await this.$store.dispatch(
        "getMainPageForDataEntry",
        this.waste.techProcessHasWasteId
      );
      this.page = this.$store.getters.GETDATAENTRYMAINPAGE;
    },

    // ---- Справочники ----

    fetchOptions(name) {
      const { dispatch, getters } = this.$store;
      if (SUBCLASSIFIERS[name])
        return dispatch("getAllDataSubClassifierForSelect", {
          id: SUBCLASSIFIERS[name],
          size: 100,
          page: 0,
        }).then((response) => response.data.content);

      const params = {
        organizationWasteId: this.waste.organizationWasteId,
        unitId: this.waste.unitId,
      };
      if (name === "contractorsByDate")
        return this.$store
          .dispatch("getContractorsByDate", this.dates[0])
          .then((list) =>
            list.map((item) => ({
              id: item.contractorId,
              name: item.contractNumber
                ? `${item.nameManual} / ${item.contractNumber}`
                : item.nameManual,
            }))
          );
      if (name === "contractors")
        return dispatch("getListContractors").then(
          () => getters.GETLISTCONTRACTORSSELECT
        );
      if (name === "transferOrgs")
        return dispatch("getTransferOrganizationList", params).then(
          () => getters.GETTRANSFERORGANIZATIONLIST
        );
      if (name === "storageOrgs")
        return dispatch("getSentForStorageOrganizationList", params).then(
          () => getters.GETSENTFORSTORAGEORGANIZATIONLIST
        );
      throw new Error("Неизвестный справочник " + name);
    },

    // Контрагенты зависят от даты (действующие договоры) - перезагружаем при смене первой даты
    optionsKey(name) {
      return name === "contractorsByDate" ? this.dates[0] : true;
    },

    async loadOptions(type) {
      const names = (OPTIONS_FOR[type] || []).filter(
        (name) => this.loaded[name] !== this.optionsKey(name)
      );
      if (!names.length) return;
      this.optionsLoading++;
      try {
        await Promise.all(
          names.map(async (name) => {
            this.options[name] = (await this.fetchOptions(name)) || [];
            this.loaded[name] = this.optionsKey(name);
          })
        );
      } catch (error) {
        this.$message.error("Не удалось загрузить справочники");
      } finally {
        this.optionsLoading--;
      }
    },

    // ---- Проверка дат и введённого ----

    // Как setDatastored в старых модалках: блок главного эколога, цепочка, остаток на дату
    async fetchCheck(date) {
      const [block, stored] = await Promise.all([
        this.$store.dispatch("getBlockForUser", date),
        this.$store.dispatch("getDataStored", {
          date,
          techProcessHasWasteId: this.waste.techProcessHasWasteId,
        }),
      ]);
      const data = stored.data;
      let problem = null;
      if (block.data === true) problem = "Заблокировано главным экологом";
      else if (data === true) problem = "Цепочка устарела";
      else if (data === false) problem = "Цепочка не начала действовать";
      this.$set(this.checks, date, {
        problem,
        available: problem ? null : data.amount - data.sumAmountGet,
        lastDate: problem ? null : data.path,
      });
    },

    // Уже идущую проверку даты не повторяем, а ждём; неудачную можно повторить
    checkDate(date) {
      if (!this.pendingChecks[date])
        this.pendingChecks[date] = this.fetchCheck(date).catch((error) => {
          delete this.pendingChecks[date];
          throw error;
        });
      return this.pendingChecks[date];
    },

    async checkDates() {
      const missing = this.dates.filter((date) => !this.checks[date]);
      await runLimited(missing, 6, (date) => this.checkDate(date));
    },

    async checkInBackground() {
      try {
        await this.checkDates();
      } catch (error) {
        // Не страшно: даты проверятся ещё раз при сохранении
        return;
      }
      this.noticeRecalculation();
    },

    noticeRecalculation() {
      if (this.recalcNoticed) return;
      const recalculates = this.dates.some((date) => {
        const check = this.checks[date];
        return check && Date.parse(check.lastDate) > Date.parse(date);
      });
      if (!recalculates) return;
      this.recalcNoticed = true;
      this.$notify({
        title: "Внимание",
        message:
          "Внесение информации за прошедшие даты приведет к перерасчету количества отходов и изменениям в отчетной документации. Будьте внимательны при заполнении!",
        type: "warning",
        duration: 10000,
      });
    },

    async validateDates() {
      if (!this.dates.length) return this.warn("Выберите хотя бы одну дату");
      try {
        await this.checkDates();
      } catch (error) {
        this.$message.error("Не удалось проверить даты, попробуйте ещё раз");
        return false;
      }
      const problems = Object.keys(this.dateProblems).sort();
      if (problems.length) {
        this.$refs.calendar.showDate(problems[0]);
        this.$message.error(
          "За даты, отмеченные красным, вносить данные нельзя (причина - при наведении). Снимите их выбор."
        );
        return false;
      }
      this.noticeRecalculation();
      return true;
    },

    rowsValid(fields, values, skippable) {
      return this.dates.every(
        (date) => rowStatus(fields, values[date], skippable).valid
      );
    },

    validateAmounts() {
      const first = this.dates.find((date) => this.amounts[date] !== undefined);
      if (!first) return this.warnInTable("Введите количество");
      // Серые предложенные значения запоминаем как внесённые
      const amounts = {};
      this.dates.forEach(
        (date) =>
          (amounts[date] =
            this.amounts[date] !== undefined
              ? this.amounts[date]
              : this.amounts[first])
      );
      this.amounts = amounts;

      if (!this.isIncoming) {
        const over = this.dates.some(
          (date) => parseFloat(amounts[date]) > this.available[date] + 0.001
        );
        if (over)
          return this.warnInTable(
            "Количество превышает доступный остаток - такие строки отмечены красным"
          );
      }
      if (!this.rowsValid(this.modeFields, this.modeValues, false)) {
        this.showErrors = true;
        return this.warnInTable("Заполните выделенные поля");
      }
      return true;
    },

    async validateEntry() {
      this.showErrors = false;
      return (await this.validateDates()) && this.validateAmounts();
    },

    // ---- Сопроводительный паспорт ----

    /*
      Номер нового паспорта: на 1 больше самого большого в этой записи
      (у передачи контрагенту - среди паспортов того же года) и не меньше следующего свободного
      на сервере. Номер в форме можно изменить - при сохранении он не перезаписывается
    */
    nextPassportNumber(date, contractor, serverNext) {
      const year = date.slice(0, 4);
      const numbers = this.dates
        .filter((other) => !contractor || other.slice(0, 4) === year)
        .map((other) => (this.detailValues[other] || {}).passport)
        .filter((passport) => passport && passport.number != null)
        .map((passport) => Number(passport.number));
      return Math.max(Number(serverNext) || 1, ...numbers.map((n) => n + 1));
    },

    // Как в «Вывозе отходов»: следующий паспорт - копия последнего заполненного с номером +1
    async openPassport(date) {
      const [y, m, d] = date.split("-");
      const contractor = this.detailType === "contractor";
      // Передача контрагенту нумерует паспорта по году, как «Вывоз отходов»
      const info = contractor
        ? await this.$store.dispatch("GetNumberPassportYear", y)
        : await this.$store.dispatch("GetNumberPassport");
      const last = this.lastPassportDate
        ? this.detailValues[this.lastPassportDate] || {}
        : {};
      const passport = last.passport
        ? JSON.parse(JSON.stringify(last.passport))
        : Object.assign(emptyPassport(), {
            egr_full_name: info.egr_full_name,
            contractor1r: info.egr_full_name,
            contractor2r: info.egr_full_name,
            contractor3r: info.egr_full_name,
          });
      Object.assign(passport, {
        number: this.nextPassportNumber(date, contractor, info.numb),
        data: `${d}.${m}.${y}`,
      });
      this.passportWastes = null;
      if (contractor) {
        // Как в accompanyingPassport: получатель и перевозчик - выбранный контрагент
        const values = this.detailValues[date] || {};
        const contractorId = values.Contractors ? values.Contractors.id : null;
        Object.assign(passport, {
          ca2: "Контрагент",
          ca3: "Контрагент",
          contractor2: contractorId,
          contractor3: contractorId,
          contractor2r: null,
          contractor3r: info.egr_full_name,
        });
        if (values.receivedFromPerson)
          Object.assign(passport, {
            ca2: "Ручной ввод",
            contractor2r: "Физическое лицо",
          });
        // Тара строки, а если её ещё нет - из последнего заполненного паспорта
        const [code, ...name] = this.waste.name.split("-");
        this.passportWastes = [
          {
            ss01DictionaryId: this.waste.organizationWasteId,
            wasteCode: code,
            wasteName: name.join("-"),
            tara_name: values.taraName || last.taraName || null,
            tara_vight: values.taraWeight || last.taraWeight || null,
          },
        ];
      }
      this.passport = passport;
      this.passportDate = date;
    },

    onPassport(passport, psId, wastes) {
      const date = this.passportDate;
      const row = { ...this.detailValues[date], passport };
      if (wastes && wastes[0])
        Object.assign(row, {
          taraName: wastes[0].tara_name,
          taraWeight: wastes[0].tara_vight,
        });
      this.detailValues = { ...this.detailValues, [date]: row };
      this.lastPassportDate = date;
      this.passportDate = null;
    },

    // ---- Сохранение ----

    // Все записи режима одним запросом, в том числе строки с сопроводительным паспортом
    async send(type, dates, values) {
      const pending = dates.filter((date) => !this.sent[type + ":" + date]);
      if (!pending.length) return;
      await this.loadPage();
      if (type === "contractor")
        return this.sendContractor(pending, values);
      const payloads = pending.map((date) =>
        buildPayload(type, {
          date,
          store: this.amounts[date],
          // Как в старых модалках: текущее количество на хранении
          amount: this.page.amount,
          values: values[date] || {},
          unitId: this.waste.unitId,
        })
      );

      await this.$store.dispatch("receiveDataEntryForm", {
        modalData: payloads,
        id: this.waste.techProcessHasWasteId,
        actionId: MODES[type].actionId,
      });
      pending.forEach((date) => this.$set(this.sent, type + ":" + date, true));
    },

    // Передача контрагенту - через /receive/formwaste, как «Вывоз отходов»
    async sendContractor(dates, values) {
      const records = dates.map((date) =>
        buildContractorPayload({
          date,
          store: this.amounts[date],
          amount: this.page.amount,
          values: values[date] || {},
          tphwId: this.waste.techProcessHasWasteId,
        })
      );

      await this.$store.dispatch("receiveContractorTransfer", records);
      dates.forEach((date) => this.$set(this.sent, "contractor:" + date, true));
    },

    async save() {
      const type = this.detailType;
      let actionDates = [];
      if (type) {
        if (!this.rowsValid(this.detailFields, this.detailValues, this.isIncoming)) {
          this.showErrors = true;
          return this.warnInTable("Заполните выделенные поля");
        }
        actionDates = this.dates.filter(
          (date) =>
            !rowStatus(this.detailFields, this.detailValues[date], this.isIncoming)
              .skip
        );
        if (!actionDates.length)
          return this.warn(
            `Не заполнено ни одной строки. Чтобы сохранить только «${MODES[this.mode].title}», вернитесь к этапу «Даты и количество» (нажмите на него вверху) и выберите «Только сохранить».`
          );
      }

      let stage = "incoming";
      try {
        if (this.isIncoming) await this.send(this.mode, this.dates, this.modeValues);
        stage = "action";
        if (type) await this.send(type, actionDates, this.detailValues);
      } catch (error) {
        if (isTimeout(error)) {
          this.$notify({
            message:
              "Выполняется сохранение данных на сервере. Время ожидания до 3 минут." +
              (type && stage === "incoming"
                ? ` «${MODES[type].title}» внесите отдельно после сохранения.`
                : ""),
            type: "warning",
            duration: 60000,
          });
          this.$emit("saved");
          return;
        }
        this.$message.error(
          this.isIncoming && stage === "action"
            ? `«${MODES[this.mode].title}» сохранено, а «${MODES[type].title}» - нет. Нажмите «Сохранить», чтобы повторить.`
            : "Не удалось сохранить данные"
        );
        return;
      }

      this.$notify({ title: "Успешно", message: "Данные внесены", type: "success" });
      this.$emit("saved");
    },
  },

  created() {
    // Без реактивности: таймер и идущие запросы проверки дат { [date]: Promise }
    this.checkTimer = null;
    this.pendingChecks = {};
  },

  beforeDestroy() {
    clearTimeout(this.checkTimer);
  },

  async mounted() {
    try {
      await this.loadPage();
    } catch (error) {
      this.$message.error("Не удалось загрузить данные отхода");
    }
  },
};
</script>

<style scoped>
.entry-wizard {
  display: flex;
  flex-direction: column;
}
.entry-wizard__steps {
  flex: 1;
  min-height: 0;
}
.entry-step {
  min-height: 200px;
}
.entry-screen {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.entry-screen__main {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
}
/*
  Экран дат не ниже календаря: если календарь не помещается по высоте,
  экран вытягивается и прокручивается вся область этапа
*/
.entry-screen.is-growing {
  height: auto;
  min-height: 100%;
}
.is-growing .entry-screen__main {
  min-height: auto;
}
/*
  Таблица растянута по обёртке и не влияет на высоту экрана:
  иначе её прокрутка пропала бы, а экран рос вместе со строками
*/
.entry-screen__table-box {
  position: relative;
  flex: 1;
  min-width: 0;
}
.entry-screen__table-box > .entry-screen__table {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
/* Календарь, таблица и кнопки - отдельные блоки в рамках */
.entry-screen__calendar,
.entry-screen__table,
.entry-screen__actions {
  box-sizing: border-box;
  /* Цвет el-divider и границы левой панели */
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
}
/* Левая колонка с календарём */
.entry-screen__side {
  flex: 0 0 38%;
  min-width: 340px;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* Рамка по высоте самого календаря, а не во всю высоту таблицы */
.entry-screen__calendar {
  flex-shrink: 0;
  padding: 12px;
}
/* Таблица без внутренних отступов: её шапка прилегает к рамке */
.entry-screen__table {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
/* Нижняя линия el-table прилегает к рамке и делала её двойной */
.entry-screen__table ::v-deep .el-table::before {
  display: none;
}
.entry-screen__actions {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  margin-top: 16px;
  padding: 12px;
}
/* «Отмена» и «Сохранить» - без рамки, справа под всеми блоками */
.entry-screen__actions.is-plain {
  justify-content: flex-end;
  padding: 0;
  border: none;
}
/* Как заголовки групп карточек («Поступление», «Расход»), значения - тёмным */
.entry-info {
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.entry-info b {
  color: #303133;
}
</style>
